import React, { useMemo } from "react";
import CenterModal from "../../UI/Modal/CenterModal";
import useLanguageChange from "../../../hooks/useLanguageChange";
import { useDispatch, useSelector } from "react-redux";
import { startScreenShare } from "./ScreenShareUtils";
import { setIsConfirmScreenShareModalOpen, setScreenShareErrorOccurredFor } from "../../../reducers/twilioEvents-reducer";
import { BROWSER } from "../../../constants/BottomMenuConstants";
import { setIsStartCameraAsPerAgentRequestPermission, setIsConfirmMultiStreamOpen } from "../../../reducers/common-reducer";
import { expandViewEnabled, isLocalUserStreaming } from "../Room/roomFunction";
import { checkLivePointerOn } from "../../LivePointer/LivePointerUtils";
import { LogFirebase } from "../../../services/firestore";
import { CALL_DATA } from "../../../constants/PublicVariables";
import { performStartStopVideo } from "../VideoCall/VideoUtils";
import { showGridView } from "../../GalleryView/GalleryViewUtils";

const ScreenSharePopUp = () => {
    const dispatch = useDispatch();
    const localisedValues = useLanguageChange();
    const { startLocalVideo, isLocalUserScreenSharing, isOpponentUserScreenSharing, isConfirmScreenShareModalOpen, screenShareErrorOccurredFor } =
        useSelector((state) => state.twilioEvents);
    const { isConfirmMultiStreamOpen } = useSelector((state) => state.commonReducer);

    const handleCloseMultiConfirmScreenShareModal = () => {
        dispatch(setIsConfirmMultiStreamOpen(false));
        dispatch(setIsStartCameraAsPerAgentRequestPermission(false));
    };

    const overrideLocalInMulstiStream = () => {
        dispatch(setIsConfirmMultiStreamOpen(false));
        if (expandViewEnabled() && isLocalUserStreaming()) {
            checkLivePointerOn();
            showGridView(CALL_DATA.localIdentity, true);
        }
        if (startLocalVideo) {
            startScreenShare();
        } else if (isLocalUserScreenSharing) {
            LogFirebase("MultiStream", "ScreenShareOff", CALL_DATA.localIdentity);
            performStartStopVideo(true);
        }
    };

    const multiStreamHeader = (
        <>
            <h5 className='modal-title' data-testid='multiStream-model'>
                {startLocalVideo ? localisedValues["share_your_screen"] : localisedValues["share_live_video"]}
            </h5>
            <button type='button' className='btn-close' aria-label='Close' onClick={() => handleCloseMultiConfirmScreenShareModal()}>
                <span className='btn-icon icon-close-light'></span>
            </button>
        </>
    );

    const multiStreamBody = (
        <div className='cms-modal-text'>
            <p>{startLocalVideo ? localisedValues["override_own_video"] : localisedValues["override_own_screen_share"]}</p>
        </div>
    );

    const multiStreamFooter = (
        <div className='button-inline more-space button-inline-tb-space d-flex align-items-center'>
            <div className='button'>
                <button className='btn default-btn-link' onClick={() => handleCloseMultiConfirmScreenShareModal()}>
                    {localisedValues["cancel_initcap"]}
                </button>
            </div>
            <div className='button'>
                <button className='btn default-btn-primary' onClick={() => overrideLocalInMulstiStream()}>
                    {localisedValues["yes_initcap"]}
                </button>
            </div>
        </div>
    );

    // <!-- --------------------- confirm screen share pop up ----------------------------- -->
    const handleCloseConfirmScreenShareModal = () => {
        dispatch(setIsConfirmScreenShareModalOpen(false));
    };

    const handleConfirmScreenShareOverride = () => {
        startScreenShare();
        handleCloseConfirmScreenShareModal();
    };

    const header = (
        <>
            <h5 className='modal-title' data-testid='share-your-screen'>
                {localisedValues["share_your_screen"]}
            </h5>
            <button type='button' className='btn-close' aria-label='Close' onClick={() => handleCloseConfirmScreenShareModal()}>
                <span className='btn-icon icon-close-light'></span>
            </button>
        </>
    );

    const body = useMemo(() => {
        let bodyText = localisedValues["override_video"];

        if (!isLocalUserScreenSharing && !isOpponentUserScreenSharing) {
            bodyText = localisedValues["override_video"];
        } else if (isOpponentUserScreenSharing) {
            bodyText = localisedValues["override_screen_share"];
        } else if (!isOpponentUserScreenSharing && isLocalUserScreenSharing) {
            bodyText = localisedValues["override_screen_share"];
        }

        return (
            <div className='cms-modal-text'>
                <p>{bodyText}</p>
            </div>
        );
    }, [localisedValues, isLocalUserScreenSharing, isOpponentUserScreenSharing]);

    const footer = (
        <div className='button-inline more-space button-inline-tb-space d-flex align-items-center'>
            <div className='button'>
                <button className='btn default-btn-link' onClick={() => handleCloseConfirmScreenShareModal()}>
                    {localisedValues["cancel_initcap"]}
                </button>
            </div>
            <div className='button'>
                <button className='btn default-btn-primary' onClick={() => handleConfirmScreenShareOverride()}>
                    {localisedValues["yes_initcap"]}
                </button>
            </div>
        </div>
    );

    // <!-- Screen share permission error message with -->
    const handleClosePermissionModal = () => {
        dispatch(setScreenShareErrorOccurredFor(false));
    };

    const permissionErrorHeader = (
        <>
            <h5 className='modal-title' data-testid='permission-error-model'>
                {localisedValues["message_title"]}
            </h5>
            <button type='button' className='btn-close' aria-label='Close' onClick={() => handleClosePermissionModal()}>
                <span className='btn-icon icon-close-light'></span>
            </button>
        </>
    );

    const permissionErrorBody = (
        <>
            {screenShareErrorOccurredFor === BROWSER.MAC && (
                <div className='cms-modal-text'>
                    <p>{localisedValues["permission_message_for_screenshare"]}</p>
                    <p>{localisedValues["tips_colon"]}</p>
                    <p className='with-inline-icon'>
                        {`${localisedValues["go_to_system_preferences"]} `} <span className='for-icon icon-next'></span>
                        {` ${localisedValues["security_privacy"]} `} <span className='for-icon icon-next'></span>
                        {` ${localisedValues["select_privacy_tab"]} `}
                        <span className='for-icon icon-next'></span> {` ${localisedValues["screen_recording"]} `}
                        <span className='for-icon icon-next'></span>
                        {` ${localisedValues["select_your_browser"]} `}
                    </p>
                </div>
            )}

            {screenShareErrorOccurredFor === BROWSER.FIREFOX && (
                <div className='cms-modal-text'>
                    <p>{localisedValues["permission_message_for_screenshare"]}</p>
                    <p>{localisedValues["tips_colon"]}</p>
                    <ul className='list-number'>
                        <li className='with-inline-icon'>
                            {` ${localisedValues["click_the_padlock"]} `}
                            <span className='for-icon icon-lock'></span> {localisedValues["to_the_left_of_a_web_page_address"]}
                        </li>
                        <li>{localisedValues["clear_blocked_permission_for_share_the_screen"]}</li>
                    </ul>
                </div>
            )}
        </>
    );

    const permissionErrorFooter = (
        <div className='button-inline more-space button-inline-tb-space d-flex align-items-center'>
            <div className='button'>
                <button className='btn default-btn-primary' onClick={() => handleClosePermissionModal()}>
                    {localisedValues["okay_initcap"]}
                </button>
            </div>
        </div>
    );

    return (
        <>
            {isConfirmScreenShareModalOpen && (
                <CenterModal
                    showModal={isConfirmScreenShareModalOpen}
                    closeModal={() => setIsConfirmScreenShareModalOpen(false)}
                    modalClass='default-modal modal-small mob-bottom-top'
                    bodyClassName='default-custom-scroll'
                    className='modal-dialog-centered'
                    header={header}
                    body={body}
                    footer={footer}
                    backdrop={"static"}
                />
            )}

            {isConfirmMultiStreamOpen && (
                <CenterModal
                    showModal={isConfirmMultiStreamOpen}
                    closeModal={() => handleCloseMultiConfirmScreenShareModal()}
                    modalClass='modal fade default-modal modal-medium mob-bottom-top show d-block'
                    bodyClassName='default-custom-scroll'
                    className='modal-dialog modal-dialog-centered'
                    header={multiStreamHeader}
                    body={multiStreamBody}
                    footer={multiStreamFooter}
                    backdrop={"static"}
                />
            )}

            {screenShareErrorOccurredFor !== false && (
                <CenterModal
                    showModal={true}
                    closeModal={() => handleClosePermissionModal()}
                    modalClass='default-modal modal-medium mob-bottom-top show d-block'
                    bodyClassName='default-custom-scroll'
                    className='modal-dialog-centered'
                    header={permissionErrorHeader}
                    body={permissionErrorBody}
                    footer={permissionErrorFooter}
                    backdrop={"static"}
                />
            )}
        </>
    );
};

export default ScreenSharePopUp;
